import { ReactComponent as AddIcon } from "assets/icons/add.svg";
import {
    Measurement,
    MeasurementType,
    measurementTypesOptions,
} from "modules/common/components/Alarms/constants";
import PhenophaseInput from "modules/common/components/Alarms/Form/components/Measurements/PhenophaseInput";
import RangeInput from "modules/common/components/Alarms/Form/components/Measurements/RangeInput";
import ThresholdInput from "modules/common/components/Alarms/Form/components/Measurements/TresholdInput";
import { FC, useContext, useMemo, useCallback } from "react";
import Select from "react-select";
import { AlarmTypeEnum } from "services";
import { AccessibilityContext } from "views/AccessibilityContext";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { RoleType, PARTIAL_PERMISSIONS_ROLES } from "modules/common/constants";

interface Props {
    selectedMeasurementType?: Measurement;
    selectedMeasurementTypes: Measurement[];
    onMinChange: (keyValue: MeasurementType, value?: number) => void;
    onMaxChange: (keyValue: MeasurementType, value?: number) => void;
    onAmountChange: (keyValue: MeasurementType, value?: number) => void;
    onLookbackDaysChange: (keyValue: MeasurementType, value?: number) => void;
    onCultureChange: (keyValue: MeasurementType, value?: number) => void;
    onPhenophaseChange: (keyValue: MeasurementType, value?: number | null) => void;
    removeMeasurementType: (keyValue: MeasurementType) => void;
    setSelectedMeasurementType: (value: any) => void;
    addMeasurement: () => void;
}

const Measurements: FC<Props> = ({
    selectedMeasurementType,
    selectedMeasurementTypes,
    setSelectedMeasurementType,
    onMinChange,
    onMaxChange,
    onAmountChange,
    onLookbackDaysChange,
    onCultureChange,
    onPhenophaseChange,
    removeMeasurementType,
    addMeasurement,
}) => {
    const rangeInputsOptions = useMemo(() => {
        const options = selectedMeasurementTypes.map(selectedMeasurement => {
            const measurementType = measurementTypesOptions.find(
                m => m.id === selectedMeasurement.id
            );
            if (AlarmTypeEnum.Regular !== measurementType?.type) {
                return null;
            }

            return {
                ...measurementType,
                min: selectedMeasurement.valueRange?.min,
                max: selectedMeasurement.valueRange?.max,
            };
        });

        return options.filter((o): o is NonNullable<typeof o> => o !== null);
    }, [selectedMeasurementTypes]);

    const thresholdInputsOptions = useMemo(() => {
        const options = selectedMeasurementTypes.map(selectedMeasurement => {
            const measurementType = measurementTypesOptions.find(
                m => m.id === selectedMeasurement.id
            );
            if (AlarmTypeEnum.ForSpecificLoockbackDays !== measurementType?.type) {
                return null;
            }

            return {
                ...measurementType,
                amount: selectedMeasurement.amount,
                lookbackDays: selectedMeasurement.lookbackDays,
            };
        });

        return options.filter((o): o is NonNullable<typeof o> => o !== null);
    }, [selectedMeasurementTypes]);

    const phenophaseInputsOptions = useMemo(() => {
        const options = selectedMeasurementTypes.map(selectedMeasurement => {
            const measurementType = measurementTypesOptions.find(
                m => m.id === selectedMeasurement.id
            );
            if (AlarmTypeEnum.Phenophase !== measurementType?.type) {
                return null;
            }

            return {
                ...measurementType,
                phenophaseId: selectedMeasurement.phenophaseId,
            };
        });

        return options.filter((o): o is NonNullable<typeof o> => o !== null);
    }, [selectedMeasurementTypes]);

    const userRole = useSelector((state: RootState) => state.signIn.user?.role);

    const hasPartialPermissions = useCallback((role?: { value: RoleType }): boolean => {
        if (!role) return false;
        return PARTIAL_PERMISSIONS_ROLES.includes(role.value);
    }, []);

    const filteredMeasurementTypes = useMemo(() => {
        let filtered = measurementTypesOptions.filter(
            m => !selectedMeasurementTypes.some(sm => sm.id === m.id)
        );

        if (hasPartialPermissions(userRole)) {
            filtered = filtered.filter(
                m => m.id !== MeasurementType.Pests && m.id !== MeasurementType.Phenophase
            );
        }

        return filtered;
    }, [selectedMeasurementTypes, userRole, hasPartialPermissions]);

    const renderRangeInputs = () => {
        return rangeInputsOptions.map(i => (
            <RangeInput
                key={i.measurementType}
                keyValue={i.id}
                title={`${i.measurementType} (${i.measurementUnit})`}
                min={i.min}
                max={i.max}
                onMaxChange={onMaxChange}
                onMinChange={onMinChange}
                removeRangeInput={removeMeasurementType}
            />
        ));
    };

    const renderThreshholdInputs = () => {
        return thresholdInputsOptions.map(i => (
            <ThresholdInput
                key={i.measurementType}
                keyValue={i.id}
                title={`${i.measurementType} (${i.measurementUnit})`}
                amount={i.amount}
                lookbackDays={i.lookbackDays}
                onAmountChange={onAmountChange}
                onLookbackDaysChange={onLookbackDaysChange}
                removeThresholdInput={removeMeasurementType}
            />
        ));
    };

    const renderPhenophaseInputs = () => {
        return phenophaseInputsOptions.map(i => (
            <PhenophaseInput
                key={i.measurementType}
                keyValue={i.id}
                title={`${i.measurementType}`}
                phenophaseId={i.phenophaseId}
                onCultureChange={onCultureChange}
                onPhenophaseChange={onPhenophaseChange}
                removePhenophaseInput={removeMeasurementType}
            />
        ));
    };
    const { lowVision } = useContext(AccessibilityContext);
    return (
        <>
            <div className="alarms-form-measurement-type-input">
                <div>
                    <label>Vrsta mjerenja/senzora</label>
                    <Select
                        key={selectedMeasurementType?.id || "empty"}
                        name="measurementType"
                        value={selectedMeasurementType}
                        placeholder="Odaberite vrstu mjerenja"
                        options={filteredMeasurementTypes}
                        getOptionLabel={s => s.measurementType}
                        getOptionValue={s => s.id}
                        onChange={s => setSelectedMeasurementType(s)}
                        className={lowVision ? "low-vision" : ""}
                        classNamePrefix="react-select"
                    />
                </div>
                <AddIcon className="alarms-form-add-icon" onClick={addMeasurement} />
            </div>

            {renderRangeInputs()}

            {renderThreshholdInputs()}

            {renderPhenophaseInputs()}
        </>
    );
};

export default Measurements;
